import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ChatModeIcon from '../icons/ChatModeIcon';
import ResearchModeIcon from '../icons/ResearchModeIcon';
import { API_CONFIG } from '../../config/api';
import './ModeSelector.css';

interface ModeOption {
  value: 'chat' | 'research';
  title: string;
  subtitle: string;
  icon: React.FC<{ fill?: string; size?: number; className?: string }>;
}

interface ModeSelectorProps {
  value: boolean; // isAgentMode: true = research, false = chat
  onChange: (value: boolean) => void;
}

const modes: ModeOption[] = [
    {
    value: 'chat',
    title: 'Chat Mode',
    subtitle: 'Ask quick questions.',
    icon: ChatModeIcon
  },
  {
    value: 'research',
    title: 'Research Mode',
    subtitle: 'Research, analyse and generate insights.',
    icon: ResearchModeIcon
  },

];

const ModeSelector: React.FC<ModeSelectorProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter modes based on feature flag
  const availableModes = API_CONFIG.FEATURES.ENABLE_CHAT_MODE
    ? modes
    : modes.filter(m => m.value === 'research');

  // Convert boolean to mode value
  const currentMode = value ? 'research' : 'chat';
  const selectedOption = availableModes.find(m => m.value === currentMode) || availableModes[0];

  // The dropdown is portalled to document.body and positioned with viewport
  // coordinates, so it is never trapped by a transformed ancestor.
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      left: rect.left
    });
  }, []);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside. The dropdown lives outside
  // dropdownRef once portalled, so it has to be excluded explicitly -
  // otherwise mousedown on an option would unmount it before its onClick
  // could fire.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = dropdownRef.current?.contains(target);
      const insideDropdown = menuRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Coordinates are captured once at open time, so the dropdown has to react
  // when the page moves underneath it. Scrolling closes it rather than
  // re-anchoring: a fixed-position menu that follows its trigger slides across
  // the viewport on every scroll frame, which reads as the menu chasing the
  // cursor and can carry it off-screen. Dismissing matches how a native select
  // behaves and leaves the trigger exactly where the user left it.
  //
  // A resize does not move content past the user, so it still re-anchors —
  // closing there would drop the menu on an incidental window drag.
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (event: Event) => {
      // Scrolling within the menu itself is not the page moving underneath it.
      if (menuRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const handleResize = () => updateDropdownPosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleSelect = (mode: ModeOption) => {
    const newAgentMode = mode.value === 'research';
    console.log('[ModeSelector] Mode selected:', mode.value, '→ isAgentMode:', newAgentMode);
    onChange(newAgentMode);
    setIsOpen(false);
  };

  // If only one mode is available, don't show the selector
  if (availableModes.length <= 1) {
    return null;
  }

  return (
    <div className="mode-selector" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        className="mode-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select mode"
      >
        <selectedOption.icon
          size={24}
          fill="#6F7680"
        />
      </button>

      {isOpen && createPortal(
        <div
          className="mode-selector-dropdown"
          ref={menuRef}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          {availableModes.map((mode) => {
            const isSelected = mode.value === currentMode;
            const IconComponent = mode.icon;

            return (
              <button
                key={mode.value}
                type="button"
                className={`mode-option ${isSelected ? 'mode-option-selected' : ''}`}
                onClick={() => handleSelect(mode)}
              >
                <div className="mode-option-icon">
                  <IconComponent
                    size={24}
                    fill={isSelected ? '#0442BF' : '#6F7680'}
                  />
                </div>
                <div className="mode-option-content">
                  <div className={`mode-option-title ${isSelected ? 'mode-option-title-selected' : ''}`}>
                    {mode.title}
                  </div>
                  <div className="mode-option-subtitle">
                    {mode.subtitle}
                  </div>
                </div>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ModeSelector;

